<template>
  <div>
    <div
      v-if="tableConfig?.groups"
      class="overflow-x-auto rounded-xl border-b-2 border-l-2 border-r-2 border-gray-200"
    >
      <table class="min-w-full divide-y divide-gray-200 text-sm select-none">
        <thead>
          <tr class="bg-gray-100 text-center">
            <template v-for="group in displayGroups" :key="group.key">
              <th
                v-if="visibleColumns && visibleColumns[group.key]"
                :colspan="group.columns?.length || 1"
                class="px-6 py-4 border-b-2 font-medium group-header rounded-t-xl bg-gray-200"
              >
                {{ group.title }}
              </th>
            </template>
          </tr>
          <tr class="text-center bg-white border-b border-gray-200">
            <template v-for="group in displayGroups" :key="group.key">
              <template v-if="visibleColumns && visibleColumns[group.key]">
                <th
                  v-for="column in group.columns"
                  :key="column.key"
                  class="px-6 py-3 text-xs text-gray-700 uppercase"
                >
                  {{ column.header }}
                </th>
              </template>
            </template>
          </tr>
        </thead>

        <tbody class="bg-white divide-y divide-gray-100">
          <tr
            v-for="(item, rowIndex) in formattedData"
            :key="rowIndex"
            class="bg-white border-b hover:bg-gray-50 text-center"
          >
            <template v-for="group in displayGroups" :key="group.key">
              <template v-if="visibleColumns && visibleColumns[group.key]">
                <td
                  v-for="column in group.columns"
                  :key="column.key"
                  class="px-6 py-2 relative overflow-visible"
                >
                  <div class="flex items-center justify-center min-h-[40px]">
                    <template v-if="column.key === 'check.verificacao'">
                      <v-btn
                        v-if="item.check.verificacao.state !== 'NONE'"
                        size="x-small"
                        :color="item.check.verificacao.state === 'BIOMETRY' ? '#6B4FA3' : '#2F6F9F'"
                        class="!text-white"
                        data-testid="report-verification-button"
                        @click.stop="openVerification(item.check.verificacao.checkId)"
                        >{{ VERIFICATION_BUTTON_LABELS[item.check.verificacao.state] }}</v-btn
                      >
                      <span v-else class="text-gray-400 text-xs">{{
                        VERIFICATION_BUTTON_LABELS.NONE
                      }}</span>
                    </template>
                    <span
                      v-else-if="
                        column.key === 'medico.nome' &&
                        item.statusVaga.coberturaStatus === CoverageStatus.UNIDENTIFIED
                      "
                      class="italic text-gray-500"
                      data-testid="report-unidentified-name"
                      >Ocupado s/ ident.</span
                    >
                    <div
                      v-else
                      class="relative group cursor-pointer flex flex-col items-center justify-center"
                      @click="
                        copyToClipboard(
                          tableConfig.getNestedValue(item, column.key),
                          rowIndex,
                          column.key
                        )
                      "
                    >
                      <div
                        class="absolute bottom-full mb-2 flex flex-col items-center transition-all duration-200 pointer-events-none z-50 w-max"
                        :class="[
                          activeTooltip === getCellId(rowIndex, column.key)
                            ? 'opacity-100 flex'
                            : 'hidden group-hover:flex opacity-0 group-hover:opacity-100'
                        ]"
                      >
                        <span
                          class="bg-[#2F7F91] text-white text-xs font-bold py-1.5 px-3 rounded shadow-lg whitespace-nowrap"
                        >
                          {{
                            activeTooltip === getCellId(rowIndex, column.key)
                              ? 'Link copiado!'
                              : 'Clique para copiar'
                          }}
                        </span>
                        <div class="w-2.5 h-2.5 bg-[#2F7F91] rotate-45 -mt-1.5"></div>
                      </div>

                      <span
                        class="transition-colors duration-200 font-medium break-all max-w-[200px] truncate cursor-pointer"
                        :class="[
                          activeTooltip === getCellId(rowIndex, column.key)
                            ? 'text-[#3491B1] underline'
                            : 'text-gray-700 group-hover:text-[#3491B1] group-hover:underline'
                        ]"
                      >
                        {{ tableConfig.getNestedValue(item, column.key) || '-' }}
                      </span>
                    </div>
                  </div>
                </td>
              </template>
            </template>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="flex items-center justify-center p-4 border-t border-gray-200" v-if="pageCount > 1">
      <v-pagination
        :model-value="currentPage"
        :length="pageCount"
        :total-visible="5"
        density="comfortable"
        active-color="#2D7A8C"
        color="grey-lighten-1"
        variant="text"
        rounded="circle"
        @update:modelValue="$emit('update:currentPage', $event)"
      ></v-pagination>
    </div>

    <CheckVerificationModal v-model:is-open="isVerificationOpen" :check-id="verificationCheckId" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, PropType, onBeforeUnmount } from 'vue'
import { format, parseISO, isValid } from 'date-fns'
import { useToast } from 'vue-toastification'
import { tableConfig } from '@/modules/company/Reports/utils/table-config'
import {
  ReportItem,
  DoctorStationApiResponse,
  ApiIdAndName,
  ApiAllocator,
  ApiPerson
} from '@/Network/Types/Responses/ReportsResponses'
import CheckVerificationModal from '@/modules/company/Dashboard/components/CheckVerificationModal.vue'
import { mapCheckVerification } from '@/modules/company/Reports/utils/verification-cell'
import { VERIFICATION_BUTTON_LABELS } from '@/constants/check-verification.constants'
import {
  COVERAGE_STATUS_LABELS,
  hasCoverageContract,
  resolveCoverageStatus
} from '@/utils/coverage'
import { CoverageStatus } from '@/Network/Types/Responses/CoverageResponses'
onBeforeUnmount(() => {
  if (timeoutId) clearTimeout(timeoutId)
})
const props = defineProps({
  data: {
    type: Array as PropType<DoctorStationApiResponse[]>,
    required: true,
    default: () => []
  },
  visibleColumns: {
    type: Object as PropType<Record<string, boolean>>,
    required: true,
    default: () => ({})
  },
  currentPage: {
    type: Number,
    required: true,
    default: 1
  },
  pageCount: {
    type: Number,
    required: true,
    default: 0
  }
})

defineEmits(['update:currentPage'])
const toast = useToast()

const activeTooltip = ref<string | null>(null)
const isVerificationOpen = ref(false)
const verificationCheckId = ref<number | null>(null)

function openVerification(id: number | null) {
  if (!id) return
  verificationCheckId.value = id
  isVerificationOpen.value = true
}
let timeoutId: ReturnType<typeof setTimeout> | null = null

const getCellId = (rowIndex: number, colKey: string) => `${rowIndex}-${colKey}`

const copyToClipboard = async (value: string, rowIndex: number, colKey: string) => {
  if (!value) return

  const textToCopy = String(value)
  const currentCellId = getCellId(rowIndex, colKey)

  try {
    await navigator.clipboard.writeText(textToCopy)
    if (timeoutId) clearTimeout(timeoutId)
    activeTooltip.value = currentCellId
    timeoutId = setTimeout(() => {
      if (activeTooltip.value === currentCellId) {
        activeTooltip.value = null
      }
    }, 2000)
  } catch (err) {
    toast.error('Erro ao copiar')
  }
}

const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return ''
  const date = parseISO(dateString)
  return isValid(date) ? format(date, 'dd/MM/yyyy') : ''
}

const formatBoolean = (value: boolean | undefined | null): string => {
  return value === true ? 'Sim' : 'Não'
}

const formatPaymentTiming = (timing: string | undefined | null): string => {
  if (!timing) return ''
  const map: Record<string, string> = {
    FLOW: 'Fluxo',
    IMMEDIATE: 'À vista'
  }
  return map[timing] || timing
}

const formatProportionalityRule = (rule: string | undefined | null): string => {
  if (!rule) return ''
  const map: Record<string, string> = {
    PROPORTIONAL_WITH_LIMIT: 'Proporcional com limite',
    PROPORTIONAL_WITHOUT_LIMIT: 'Proporcional sem limite',
    INTEGRAL: 'Integral'
  }
  return map[rule] || rule
}

const isApiIdAndName = (obj: unknown): obj is ApiIdAndName =>
  !!obj && typeof obj === 'object' && 'id' in obj && 'name' in obj

const safeString = (value: unknown): string => value?.toString() ?? ''

const mapPlantao = (apiData: DoctorStationApiResponse) => ({
  id: safeString(apiData.stationId),
  data: formatDate(apiData.stationDate),
  fuso: apiData.stationTimezone ?? ''
})
const mapEmpresa = (apiData: DoctorStationApiResponse) => ({
  id: safeString(apiData.company?.id),
  nome: apiData.company?.name ?? '',
  cnpj: apiData.company?.cnpj ?? ''
})
const mapUnidade = (apiData: DoctorStationApiResponse) => ({
  id: safeString(apiData.healthUnit?.id),
  nome: apiData.healthUnit?.name ?? '',
  endereco: apiData.healthUnit?.address ?? '',
  cidade: apiData.healthUnit?.city ?? '',
  uf: apiData.healthUnit?.state ?? '',
  grupoOperacional: apiData.healthUnit?.operationalGroup ?? null
})
const mapIdAndName = (obj: unknown) => ({
  id: isApiIdAndName(obj) ? safeString(obj.id) : '',
  nome: isApiIdAndName(obj) ? obj.name : ''
})
const mapMedico = (apiData: DoctorStationApiResponse) => ({
  id: safeString(apiData.doctor?.id),
  nome: apiData.doctor?.name ?? '',
  crm: apiData.doctor?.crm ?? '',
  crmUf: apiData.doctor?.crmUf ?? '',
  email: apiData.doctor?.email ?? '',
  telefone: apiData.doctor?.phone ?? '',
  cpf: apiData.doctor?.cpf ?? '',
  equipe: apiData.doctor?.team ?? ''
})
const mapCheckEntry = (
  entry:
    | NonNullable<DoctorStationApiResponse['checkDetails']>['checkin']
    | NonNullable<DoctorStationApiResponse['checkDetails']>['checkout']
    | null
) => {
  if (!entry) return null
  return {
    data: entry.date || '',
    horario: entry.time || '',
    latitude: safeString(entry.latitude),
    longitude: safeString(entry.longitude),
    distancia: safeString(entry.distanceFromUnit),
    link: entry.geolocationLink || ''
  }
}
const mapPerson = (person: ApiPerson | ApiAllocator | null | undefined) => ({
  id: safeString(person?.id),
  nome: person?.name ?? '',
  email: person?.email ?? ''
})

const mapAntecipe = (advance: DoctorStationApiResponse['advance'] | null | undefined) => ({
  solicitada: formatBoolean(advance?.requested),
  paga: formatBoolean(advance?.paid),
  dataSolicitacao: '',
  dataPagamento: ''
})

const mapStatusVaga = (status: DoctorStationApiResponse['status'] | null | undefined) => ({
  checado: formatBoolean(status?.isChecked),
  termoAceito: formatBoolean(status?.acceptanceTerm),
  aberta: formatBoolean(status?.isOpenVacancy),
  createdAt: formatDate(status?.vacancyCreatedAt),
  updatedAt: formatDate(status?.vacancyUpdatedAt)
})

const mapAssumedData = (apiData: DoctorStationApiResponse) => {
  const source = apiData.effectiveData || apiData.baseData
  const start = formatDate(source?.journeyStart)
  const end = formatDate(source?.journeyEnd)
  return {
    valor: String(source?.hourlyRate ?? ''),
    jornada: start && end ? `${start} - ${end}` : start || end || '',
    modalidade: formatPaymentTiming(source?.paymentTiming),
    regra: formatProportionalityRule(source?.proportionalityRule)
  }
}
const mapCoverage = (apiData: DoctorStationApiResponse) => {
  if (!hasCoverageContract(apiData)) {
    return { cobertura: '', coberturaStatus: null, responsavel: '', observacaoCobertura: '' }
  }
  const status = resolveCoverageStatus(apiData)
  return {
    cobertura: COVERAGE_STATUS_LABELS[status],
    coberturaStatus: status,
    responsavel: apiData.coverageResponsibleName ?? '',
    observacaoCobertura: apiData.coverageNote ?? ''
  }
}

/**
 * UNIDENTIFIED: o valor sai de "Valor Plantão" (fora dos totais) e vai para
 * "Valor de Referência", preferindo o `baseData.referenceValue` do BE.
 */
const mapAssumedDataWithCoverage = (apiData: DoctorStationApiResponse) => {
  const assumed = mapAssumedData(apiData)
  const unidentified =
    hasCoverageContract(apiData) && resolveCoverageStatus(apiData) === CoverageStatus.UNIDENTIFIED
  if (!unidentified) return { ...assumed, valorReferencia: '' }
  return {
    ...assumed,
    valor: '',
    valorReferencia: String(apiData.baseData?.referenceValue ?? assumed.valor)
  }
}

const mapApiResponseToReportItem = (apiData: DoctorStationApiResponse): ReportItem => ({
  plantao: mapPlantao(apiData),
  empresa: mapEmpresa(apiData),
  unidade: mapUnidade(apiData),
  escala: {
    ...mapIdAndName(apiData.schedule),
    grupoOperacional: apiData.schedule?.operationalGroup ?? null
  },
  posicao: mapIdAndName(apiData.position),
  turno: mapIdAndName(apiData.shift),
  medico: mapMedico(apiData),
  autorizador: mapPerson(apiData.paymentPolicy?.responsible),
  antecipe: mapAntecipe(apiData.advance),
  statusVaga: { ...mapStatusVaga(apiData.status), ...mapCoverage(apiData) },

  alocacao: {
    data: formatDate(apiData.allocator?.date),
    alocador: mapPerson(apiData.allocator)
  },
  aVista: {
    isVista: formatBoolean(apiData.immediatePayment?.isImmediate),
    dataSolicitacao: formatDate((apiData.immediatePayment as any)?.requestDate),
    liberador: mapPerson(apiData.immediatePayment?.responsible)
  },
  politica: {
    id: safeString(apiData.paymentPolicy?.id),
    nome: apiData.paymentPolicy?.name ?? '',
    inicioVigencia: formatDate(apiData.paymentPolicy?.startDate),
    fimVigencia: formatDate(apiData.paymentPolicy?.endDate),
    vinculador: mapPerson(apiData.paymentPolicy?.responsible)
  },
  excepcionalidade: {
    isExcepcionalidade: formatBoolean(!!apiData.exceptionData),
    id: safeString(apiData.exceptionData?.id),
    solicitante: mapPerson(apiData.exceptionData?.solicitante),
    aprovador: mapPerson(apiData.exceptionData?.aprovador)
  },
  dadosAssumidos: mapAssumedDataWithCoverage(apiData),
  check: {
    metodo: apiData.checkDetails?.checkMethod ?? '',
    checkin: mapCheckEntry(apiData.checkDetails?.checkin ?? null),
    checkout: mapCheckEntry(apiData.checkDetails?.checkout ?? null),
    status: apiData.checkDetails?.checkStatus ?? '',
    verificacao: mapCheckVerification(apiData.checkDetails)
  },
  pagamentoFinal: {
    valorAPagar: String(apiData.paymentValue ?? ''),
    observacoes: apiData.observations ?? ''
  }
})

const hasCoverageColumns = computed(() =>
  (props.data ?? []).some((row) => hasCoverageContract(row))
)

/** Sem `coverageStatus` na resposta, as colunas novas não existem (tabela de hoje). */
const displayGroups = computed(() =>
  tableConfig.groups.map((group) => ({
    ...group,
    columns: group.columns.filter(
      (column: { requiresCoverage?: boolean }) =>
        hasCoverageColumns.value || !column.requiresCoverage
    )
  }))
)

const formattedData = computed(() => {
  if (!props.data || !Array.isArray(props.data)) return []
  return props.data.map(mapApiResponseToReportItem)
})
</script>

<style scoped>
.select-none {
  user-select: none;
}
.group-header {
  border-right: 2px solid white;
}
thead tr th.group-header:last-of-type {
  border-right: none;
}
</style>
